/*
 * Lightweight, reversible obfuscation for client-side persistence.
 *
 * IMPORTANT: this is NOT cryptographic security. The key is baked into the
 * bundle, so a determined user can reverse it. Its only jobs are:
 *   1. Keep game state out of plain sight in localStorage (no readable answers).
 *   2. Make casual tampering detectable via a checksum, so we can discard
 *      hand-edited state instead of trusting it.
 *
 * For real anti-cheat you need a server that owns the answer — which this
 * no-backend daily game deliberately does not have.
 */

const SECRET = 'lnp§v2§5f3a9c' // bundle-baked; obfuscation salt only

function fnv1a(str: string): string {
  let h = 2166136261 >>> 0
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0).toString(36)
}

function xorBytes(data: Uint8Array, key: string): Uint8Array {
  const kb = new TextEncoder().encode(key)
  const out = new Uint8Array(data.length)
  for (let i = 0; i < data.length; i++) out[i] = data[i] ^ kb[i % kb.length]
  return out
}

function bytesToB64(bytes: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/**
 * Encodes an object to a `version.checksum.payload` string. `salt` (e.g. the
 * day + category) varies the obfuscation per puzzle so blobs aren't comparable.
 */
export function encodeState(obj: unknown, salt: string): string {
  const key = `${SECRET}:${salt}`
  const json = JSON.stringify(obj)
  const payload = bytesToB64(xorBytes(new TextEncoder().encode(json), key))
  return `2.${fnv1a(payload + key)}.${payload}`
}

/** Reverses {@link encodeState}. Returns null on tamper, corruption, or version mismatch. */
export function decodeState<T>(str: string, salt: string): T | null {
  try {
    const parts = str.split('.')
    if (parts.length !== 3 || parts[0] !== '2') return null
    const [, sum, payload] = parts
    const key = `${SECRET}:${salt}`
    if (fnv1a(payload + key) !== sum) return null // tampered or corrupt
    const json = new TextDecoder().decode(xorBytes(b64ToBytes(payload), key))
    return JSON.parse(json) as T
  } catch {
    return null
  }
}
